import { motion } from "framer-motion";
import { Room, RoomEvent } from "livekit-client";
import { Mic, MicOff } from "lucide-react";
import * as React from "react";

function useLocalMicActivity(room: Room, isActiveSession: boolean, isMuted?: boolean) {
  const [micActivityLevel, setMicActivityLevel] = React.useState(0);

  React.useEffect(() => {
    if (!isActiveSession || isMuted) {
      setMicActivityLevel(0);
      return;
    }

    const updateFromActiveSpeakers = (
      speakers: Array<{ sid?: string; identity?: string; audioLevel?: number }>
    ) => {
      const localParticipant = room.localParticipant;
      const localSpeaker = speakers.find(
        (speaker) =>
          speaker.sid === localParticipant.sid || speaker.identity === localParticipant.identity
      );
      setMicActivityLevel(
        localSpeaker?.audioLevel ? Math.min(1, Math.max(0, localSpeaker.audioLevel)) : 0
      );
    };

    room.on(RoomEvent.ActiveSpeakersChanged, updateFromActiveSpeakers);
    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, updateFromActiveSpeakers);
    };
  }, [isActiveSession, isMuted, room]);

  return micActivityLevel;
}

/** The session microphone toggle, which swells slightly while it hears the learner. */
export function SessionMicButton({
  room,
  isActiveSession,
  isMuted,
  onToggleMute,
  isZenMode = false,
}: {
  room: Room;
  isActiveSession: boolean;
  isMuted?: boolean;
  onToggleMute: () => void;
  isZenMode?: boolean;
}) {
  const micActivityLevel = useLocalMicActivity(room, isActiveSession, isMuted);
  const normalizedMicActivity = Math.min(1, Math.max(0, micActivityLevel));
  const [visualMicLevel, setVisualMicLevel] = React.useState(0);

  React.useEffect(() => {
    if (isMuted) {
      setVisualMicLevel(0);
      return;
    }

    const targetLevel = normalizedMicActivity < 0.08 ? 0 : normalizedMicActivity;
    setVisualMicLevel((currentLevel) => {
      const smoothing = targetLevel > currentLevel ? 0.42 : 0.18;
      const nextLevel = currentLevel + (targetLevel - currentLevel) * smoothing;
      return nextLevel < 0.015 ? 0 : Math.min(1, Math.max(0, nextLevel));
    });
  }, [isMuted, normalizedMicActivity]);

  const isHearingUser = !isMuted && visualMicLevel > 0.12;
  const speakingIntensity = isHearingUser ? Math.min(1, (visualMicLevel - 0.12) / 0.58) : 0;
  const micStatusLabel = isMuted ? "Muted" : isHearingUser ? "Hearing you" : "Listening";
  const micButtonScale = isMuted ? 1 : 1 + speakingIntensity * 0.018;
  const micInteractiveScale = isMuted ? 1.02 : 1.02 + speakingIntensity * 0.018;

  return (
    <motion.button
      animate={{ scale: micButtonScale }}
      transition={{ type: "spring", stiffness: 160, damping: 18, mass: 0.45 }}
      whileFocus={{ scale: micInteractiveScale }}
      whileHover={{ scale: micInteractiveScale }}
      whileTap={{ scale: 0.99 }}
      data-session-mic
      onClick={onToggleMute}
      className={`pointer-events-auto relative isolate flex h-10 w-10 shrink-0 origin-center items-center justify-center overflow-visible rounded-md border outline-none transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-orange-300/45 ${
        isMuted
          ? "border-red-500/35 bg-red-500/15 text-red-300 hover:border-red-400/50 hover:bg-red-500/25"
          : isZenMode
            ? "border-white/15 bg-white/[0.07] text-white/80 hover:border-white/25 hover:bg-white/[0.1]"
            : "border-orange-300/35 bg-orange-400/10 text-orange-200 hover:border-orange-300/55 hover:bg-orange-400/15"
      }`}
      aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
      title={isMuted ? "Unmute microphone" : "Mute microphone"}
    >
      <span className="sr-only">{micStatusLabel}</span>
      {isHearingUser && (
        <motion.span
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 0.55, scale: 1.12 }}
          className={`absolute -inset-1 rounded-md border ${
            isZenMode ? "border-white/25" : "border-orange-300/45"
          }`}
        />
      )}
      {isMuted ? (
        <MicOff className="relative z-10" size={17} />
      ) : (
        <Mic className="relative z-10 drop-shadow-[0_0_5px_rgba(255,255,255,0.28)]" size={17} />
      )}
    </motion.button>
  );
}
